import { UseQueryResult } from "@tanstack/react-query";
import {
	createContext,
	useContext,
	useEffect,
	useState,
	useMemo,
	PropsWithChildren
} from "react";
import { useCurrentFreelancer, useFreelancer } from "../hooks/useFreelancer";
import { useFreelancerProfile } from "../hooks/useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../models/enums/FreelancerOnboardingFormStep";
import { Freelancer } from "../models/Freelancer";
import { FreelancerProfile } from "../models/FreelancerProfile";
import { FreelancerOnboardingMyExpertiseStepViewModel } from "../ViewModels/FreelancerOnboardingMyExpertiseStepViewModel";
import {
	FreelancerOnboardingMyPresentationExperienceViewModel,
	FreelancerOnboardingMyPresentationStepViewModel
} from "../ViewModels/FreelancerOnboardingMyPresentationStepViewModel";
import { FreelancerOnboardingMySectorsStepViewModel } from "../ViewModels/FreelancerOnboardingMySectorsStepViewModel";
import { FreelancerOnboardingMySkillsStepViewModel } from "../ViewModels/FreelancerOnboardingMySkillsStepViewModel";
import { FreelancerOnboardingMyAvailabilityStepViewModel } from "../ViewModels/FreelancerOnboardingMyAvailabilityStepViewModel";
import { FreelancerOnboardingMyPersonalInformationStepViewModel } from "../ViewModels/FreelancerOnboardingMyPersonalInformationStepViewModel";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";
import { useLocation } from "react-router-dom";

export type FreelancerContextType = {
	readonly currentOnboardingFormStep: FreelancerOnboardingFormStep;
	readonly nextOnboardingFormStep: FreelancerOnboardingFormStep;
	readonly freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel | null;
	readonly freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel | null;
	readonly freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel | null;
	readonly freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel | null;
	readonly freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel | null;
	readonly freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel | null;
	readonly freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel | null;
	readonly freelancerQueryResult: UseQueryResult<Freelancer | null, Error>;
	readonly freelancerProfileQueryResult: UseQueryResult<FreelancerProfile | null, Error>;
	readonly newFreelancerProfileProfessionalExperience: FreelancerOnboardingMyPresentationExperienceViewModel | null;
	readonly showInvalidMissionEndDateDialog: boolean;
	readonly invalidMissionEndDateNextStep: FreelancerOnboardingFormStep | null;
	setCurrentOnboardingFormStep: (
		freelancerOnboardingStep: FreelancerOnboardingFormStep
	) => void;
	setNextOnboardingFormStep: (
		freelancerOnboardingStep: FreelancerOnboardingFormStep
	) => void;
	setShowInvalidMissionEndDateDialog: (show: boolean) => void;
	setInvalidMissionEndDateNextStep: (step: FreelancerOnboardingFormStep | null) => void;
	setFreelancerOnboardingMyExpertiseStepViewModel: (
		freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel | null
	) => void;
	setFreelancerOnboardingMySkillsStepViewModel: (
		freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel | null
	) => void;
	setFreelancerOnboardingMySectorsStepViewModel: (
		freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel | null
	) => void;
	setFreelancerOnboardingMyPresentationStepViewModel: (
		freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel | null
	) => void;
	setFreelancerOnboardingMyAvailabilityStepViewModel: (
		freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel | null
	) => void;
	setFreelancerOnboardingMyProfessionalInformationStepViewModel: (
		freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel | null
	) => void;
	setFreelancerOnboardingMyPersonalInformationStepViewModel: (
		freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel | null
	) => void;
	readonly saveTranslationsAsync: (() => Promise<void>) | null;
	setSaveTranslationsAsync: (fn: (() => Promise<void>) | null) => void;
};

const FreelancerContext = createContext<FreelancerContextType | undefined>(undefined);

type FreelancerProviderProps = {
	/**
	 * False for users who are inside this provider without owning a freelancer profile:
	 * app.tsx also mounts it for TAM users. Without it, every CSM navigation fires a
	 * /freelancers/me that can only answer 404. Defaults to true so the only caller that
	 * has to care is the one that knows the user type.
	 */
	readonly hasOwnFreelancerProfile?: boolean;
};

export const FreelancerProvider = ({
	children,
	hasOwnFreelancerProfile = true
}: PropsWithChildren<FreelancerProviderProps>) => {
	const contextInitialValues: Partial<FreelancerContextType> = {
		currentOnboardingFormStep: FreelancerOnboardingFormStep.MyProfile,
		nextOnboardingFormStep: FreelancerOnboardingFormStep.MyProfile,
		freelancerOnboardingMyExpertiseStepViewModel: null,
		freelancerOnboardingMySkillsStepViewModel: null,
		freelancerOnboardingMySectorsStepViewModel: null,
		freelancerOnboardingMyPresentationStepViewModel: null,
		freelancerOnboardingMyAvailabilityStepViewModel: null,
		freelancerOnboardingMyProfessionalInformationStepViewModel: null,
		freelancerOnboardingMyPersonalInformationStepViewModel: null,
		newFreelancerProfileProfessionalExperience: null,
		showInvalidMissionEndDateDialog: false,
		invalidMissionEndDateNextStep: null,
		saveTranslationsAsync: null
	};
	const [freelancerContext, setFreelancerContext] = useState<FreelancerContextType>(
		contextInitialValues as FreelancerContextType
	);
	const location = useLocation();

	// Extract the key from the URL path since this component is outside the route context
	// Exclude known route segments like "onboarding" which are not freelancer keys
	const pathMatch = location.pathname.match(/^\/profile\/([^/]+)/);
	const capturedSegment = pathMatch ? pathMatch[1] : undefined;
	const freelancerKey = capturedSegment === "onboarding" ? undefined : capturedSegment;

	const currentFreelancerQueryResult = useCurrentFreelancer(
		freelancerKey === undefined && hasOwnFreelancerProfile
	);
	let freelancerQueryResult = useFreelancer(0, freelancerKey);

	if (freelancerKey === undefined) {
		freelancerQueryResult = currentFreelancerQueryResult;
	}

	const freelancerProfileQueryResult = useFreelancerProfile(
		(currentFreelancerQueryResult.data ?? freelancerQueryResult.data)?.profile.id ?? 0
	);

	freelancerContext.setCurrentOnboardingFormStep = (
		freelancerOnboardingStep: FreelancerOnboardingFormStep
	) => {
		setFreelancerContext(previousState => {
			return { ...previousState, currentOnboardingFormStep: freelancerOnboardingStep };
		});
	};

	freelancerContext.setNextOnboardingFormStep = (
		freelancerOnboardingStep: FreelancerOnboardingFormStep
	) => {
		setFreelancerContext(previousState => {
			return { ...previousState, nextOnboardingFormStep: freelancerOnboardingStep };
		});
	};

	freelancerContext.setShowInvalidMissionEndDateDialog = (show: boolean) => {
		setFreelancerContext(previousState => {
			return { ...previousState, showInvalidMissionEndDateDialog: show };
		});
	};

	freelancerContext.setInvalidMissionEndDateNextStep = (
		step: FreelancerOnboardingFormStep | null
	) => {
		setFreelancerContext(previousState => {
			return { ...previousState, invalidMissionEndDateNextStep: step };
		});
	};

	freelancerContext.setFreelancerOnboardingMyExpertiseStepViewModel = (
		freelancerOnboardingMyExpertiseStepViewModel: FreelancerOnboardingMyExpertiseStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMyExpertiseStepViewModel
			};
		});
	};

	freelancerContext.setFreelancerOnboardingMySkillsStepViewModel = (
		freelancerOnboardingMySkillsStepViewModel: FreelancerOnboardingMySkillsStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMySkillsStepViewModel
			};
		});
	};
	freelancerContext.setFreelancerOnboardingMySectorsStepViewModel = (
		freelancerOnboardingMySectorsStepViewModel: FreelancerOnboardingMySectorsStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMySectorsStepViewModel
			};
		});
	};

	freelancerContext.setFreelancerOnboardingMyPresentationStepViewModel = (
		freelancerOnboardingMyPresentationStepViewModel: FreelancerOnboardingMyPresentationStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMyPresentationStepViewModel
			};
		});
	};

	freelancerContext.setSaveTranslationsAsync = (fn: (() => Promise<void>) | null) => {
		// Use object wrapper to avoid React interpreting a function as a state updater
		setFreelancerContext(previousState => {
			return { ...previousState, saveTranslationsAsync: fn };
		});
	};

	freelancerContext.setFreelancerOnboardingMyAvailabilityStepViewModel = (
		freelancerOnboardingMyAvailabilityStepViewModel: FreelancerOnboardingMyAvailabilityStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMyAvailabilityStepViewModel
			};
		});
	};

	freelancerContext.setFreelancerOnboardingMyProfessionalInformationStepViewModel = (
		freelancerOnboardingMyProfessionalInformationStepViewModel: FreelancerOnboardingMyProfessionalInformationStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMyProfessionalInformationStepViewModel
			};
		});
	};

	freelancerContext.setFreelancerOnboardingMyPersonalInformationStepViewModel = (
		freelancerOnboardingMyPersonalInformationStepViewModel: FreelancerOnboardingMyPersonalInformationStepViewModel | null
	) => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerOnboardingMyPersonalInformationStepViewModel
			};
		});
	};

	useEffect(() => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				freelancerQueryResult: freelancerQueryResult,
				freelancerProfileQueryResult: freelancerProfileQueryResult,
				newFreelancerProfileProfessionalExperience: {
					freelancerProfileId: freelancerProfileQueryResult.data?.id ?? 0,
					isTopExperience: false,
					companyName: "",
					sector: null,
					jobTitle: "",
					jobType: null,
					startDate: "",
					endDate: "",
					isCurrentPosition: false,
					jobLocation: null,
					missionDescription: "",
					results: "",
					technicalStack: "",
					skills: [],
					businessDomains: [],
					documents: [],
					urls: [],
					city: null
				}
			};
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [freelancerQueryResult.data, freelancerProfileQueryResult.data]);

	useEffect(() => {
		setFreelancerContext(previousState => {
			return {
				...previousState,
				currentOnboardingFormStep:
					freelancerProfileQueryResult.data?.onboarding?.formsProgress.currentStep ??
					previousState.currentOnboardingFormStep,
				nextOnboardingFormStep:
					freelancerProfileQueryResult.data?.onboarding?.formsProgress.currentStep ??
					previousState.currentOnboardingFormStep
			};
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [freelancerProfileQueryResult.data?.onboarding?.formsProgress.currentStep]);

	// Merge state with query results directly to ensure they're always available
	const contextValue = useMemo(
		() => ({
			...freelancerContext,
			freelancerQueryResult,
			freelancerProfileQueryResult
		}),
		[freelancerContext, freelancerQueryResult, freelancerProfileQueryResult]
	);

	return (
		<FreelancerContext.Provider value={contextValue}>
			{children}
		</FreelancerContext.Provider>
	);
};

export const useFreelancerContext = () => {
	const context = useContext(FreelancerContext);

	if (!context) {
		throw new Error("useFreelancerContext must be used within FreelancerContext");
	}

	return context;
};
